import React, {PureComponent} from 'react';
import {arrayOf, bool, func, oneOf, shape, string} from 'prop-types';
import {omit, min, path, findIndex, identical} from 'ramda';
import classNames from 'classnames';
import Icon from '../../../components/Icon';
import {iosArrowDown} from 'react-icons-kit/ionicons/iosArrowDown';
import {ic_check as checkmarkIcon} from 'react-icons-kit/md/ic_check';
import onClickOutside from 'react-onclickoutside';
import {optionValue} from '../Option';

import styles from './Select.scss';
import stylesV2 from './SelectV2.scss';

const preventDefault = event => event.preventDefault();

const selectedOptionIcons = {
  checkmark: checkmarkIcon,
};

// a -> OptionComponent[] -> OptionComponent
const findComponentWithValue = (value, components) =>
  value != null ? components.find(({props}) => value === props.value) : null;

const getMenuHeight = (numberOfMenuItems) => {
  const padding = parseInt(stylesV2.menuPadding, 10) * 2;
  const margin = parseInt(stylesV2.menuMargin, 10) * 2;
  const optionHeight = parseInt(stylesV2.optionHeight, 10);
  const maxHeight = parseInt(stylesV2.menuMaxHeight, 10);

  const whitespace = padding + margin;
  const contentHeight = min(maxHeight, numberOfMenuItems * optionHeight);
  return contentHeight + whitespace;
};

const getMenuPosition = (selectRef, numberOfMenuItems) => {
  const {top, bottom} = selectRef.getBoundingClientRect();
  const menuHeight = getMenuHeight(numberOfMenuItems);
  const {innerHeight} = window;

  if (bottom + menuHeight <= innerHeight) {
    return 'below';
  } else if (top - menuHeight > 0) {
    return 'above';
  } else {
    return 'fill';
  }
};

const getMenuStyles = (position) => {
  const positions = {
    below: {top: '100%'},
    above: {bottom: '100%'},
    fill: {position: 'fixed', top: '0', bottom: '0', width: 'inherit'},
  };

  return positions[position];
};

/**
 * Returns the next option after index whose label starts with labelPrefix
 * @param {number} startWithIndex
 * @param {string} labelPrefix
 * @param {OptionComponent[]} options
 * @returns {OptionComponent}
 */
const findNextOption = (startWithIndex, labelPrefix, options) => {
  const hasPrefix = option => (option.props.children || option.props.label).toLowerCase().startsWith(labelPrefix);

  const nextOption = options.find((option, index) => {
    return index >= startWithIndex && hasPrefix(option);
  });

  const prevOption = options.find((option, index) => {
    return index < startWithIndex && hasPrefix(option);
  });

  return nextOption || prevOption;
};

class Select extends PureComponent {
  static propTypes = {
    className: string,
    name: string,
    disabled: bool,
    theme: oneOf(['default', 'v2']),
    children: arrayOf(shape({
      type: func,
    })).isRequired,
    value: optionValue, // providing a value makes Select act as a controlled component
    defaultValue: optionValue,
    selectedOptionIcon: oneOf(Object.keys(selectedOptionIcons)),
    onChange: func,
    onChangeValue: func,
  }

  static defaultProps = {
    className: '',
    disabled: false,
    theme: 'default',
    selectedOptionIcon: null,
  }

  static state = {
    isMenuOpen: false,
    selectedOption: null,
    menuPosition: 'bottom',
  }

  constructor(props) {
    super(props);
    const {defaultValue, children} = props;

    this.state = {
      isMenuOpen: false,
      selectedOption: findComponentWithValue(defaultValue, children),
      menuPosition: 'bottom',
    };
  }

  handleClickOutside = () => this.props.theme === 'v2' && this.toggleMenu(false);

  toggleMenu = (isMenuOpen) => {
    const menuPosition = getMenuPosition(this.selectRef, this.props.children.length);
    const checkAndScroll = () => {
      if (this.state.isMenuOpen) {
        this.selectedRef?.scrollIntoView(true);
      }
    };

    if (isMenuOpen != null) {
      this.setState({isMenuOpen, menuPosition}, checkAndScroll);
    } else {
      this.setState({isMenuOpen: !this.state.isMenuOpen, menuPosition}, checkAndScroll);
    }
  }

  onClickSelect = () => this.toggleMenu();

  onKeyUpSelect = ({key}) => {
    if (key === ' ') {
      this.toggleMenu();
    } else if (key === 'Escape') {
      this.toggleMenu(false);
    } else if (key.length) {
      this.selectOptionByCharacter(key);
    }
  }

  onClickOption = (option) => {
    this.setOption(option);
    this.selectRef.focus();
  }

  onKeyUpOption = (option) => ({key}) => {
    if (key === ' ' || key === 'Enter') {
      this.setOption(option);
      this.selectRef.focus();
    } else if (key === 'Escape') {
      this.toggleMenu(false);
      this.selectRef.focus();
    }
  }

  setOption = (option) => {
    const {name, onChange, onChangeValue} = this.props;

    this.setState({
      isMenuOpen: false,
      selectedOption: option,
    });

    onChange && onChange({
      target: {
        name,
        value: option.props.value,
      },
    });

    onChangeValue && onChangeValue(option.props.value);
  }

  selectOptionByCharacter = (key) => {
    const {value, children} = this.props;
    const isValidLetter = /\w/.test(key);

    if (!isValidLetter) {
      return;
    }

    const letter = key.toLowerCase();
    const selectedOption = value ? findComponentWithValue(value, children) : this.state.selectedOption;
    const selectedIndex = findIndex(identical(selectedOption), children);
    const nextOption = findNextOption(selectedIndex + 1, letter, children);

    if (nextOption) {
      this.setOption(nextOption);
    }
  }

  render = () => this.props.theme === 'v2' ? this.renderV2() : this.renderV1();

  renderV1() {
    const {className, disabled} = this.props;

    return (
      <div className={classNames(styles.select, className, {[styles.disabled]: disabled})}>
        <select {...omit([
          'className',
          'theme',
          'selectedOptionIcon',
          'onChangeValue',
          'disableOnClickOutside',
          'enableOnClickOutside',
          'eventTypes',
          'stopPropagation',
          'preventDefault',
          'outsideClickIgnoreClass',
        ], this.props)} />
      </div>
    );
  }

  renderV2() {
    const {className, disabled, children, value} = this.props;
    const {isMenuOpen} = this.state;
    const containerClass = classNames(stylesV2.container, className, {
      [stylesV2.disabled]: disabled,
    });
    const selectedOption = value ? findComponentWithValue(value, children) : this.state.selectedOption;
    const selectedLabel = selectedOption ? (selectedOption.props.children || selectedOption.props.label) : null;
    const selectedIcon = path(['props', 'icon'], selectedOption);

    return (
      <div className={containerClass} role="listbox" onKeyPress={preventDefault}>
        <div
          tabIndex="0"
          ref={el => this.selectRef = el}
          className={stylesV2.select}
          onClick={this.onClickSelect}
          onKeyUp={this.onKeyUpSelect}>
          <div className={stylesV2.selectedLabel}>
            {selectedIcon && <Icon className={stylesV2.selectedIcon} icon={selectedIcon} size="14" />}
            {selectedLabel}
          </div>
          <Icon icon={iosArrowDown} className={stylesV2.caret} />
        </div>
        {isMenuOpen &&
          <div className={stylesV2.menu} style={getMenuStyles(this.state.menuPosition)}>
            {children.map(this.renderOption(selectedOption))}
          </div>
        }
      </div>
    );
  }


  renderOption = (selectedOption) => (option) => {
    const {selectedOptionIcon} = this.props;
    const isSelected = selectedOption && selectedOption.props.value === option.props.value;
    const classes = classNames(stylesV2.option, {
      [stylesV2.selectedOption]: isSelected,
    });

    return (
      <div
        key={option.props.label}
        tabIndex="0"
        role="option"
        className={classes}
        onClick={() => this.onClickOption(option)}
        onKeyUp={this.onKeyUpOption(option)}
        ref = {isSelected && ((el) => this.selectedRef = el)}>
        {selectedOptionIcon &&
          <span className={stylesV2.selectedOptionIcon}>
            {isSelected && <Icon icon={selectedOptionIcons[selectedOptionIcon]} size="14" />}
          </span>
        }
        {option}
      </div>
    );
  }
}

export {Select as UnwrappedSelect};
export default onClickOutside(Select);
